import { defineStore } from 'pinia'
import { TemplateBackgroundTypeEnum, TemplateTypeEnum, OrientationEnum } from 'src/common/enums'
import type {
  ITemplateLayer,
  ITemplateLayerItem,
  ITemplateFieldGroups,
  TemplateContentType,
  TemplateSizeType,
  ITemplate,
  TemplateItemLayerValue,
} from 'src/common/interfaces'

interface IActiveLayer {
  index: number
  id?: number | string
  updatedFrom?: 'section' | null
}

type Background = string | { blob: string; file: File }

interface IState {
  templateName: string
  templateType: TemplateTypeEnum
  orientation: OrientationEnum
  backgroundType: TemplateBackgroundTypeEnum
  background: Background
  colours: string[]
  layers: ITemplateLayer[]
  uploadedImageIds: number[]
  newUploadImages: Record<string, { file?: File; blob?: string }>
  activeLayer: IActiveLayer
  activeItem?: ITemplateLayerItem | Record<string, unknown>
  templateSize: TemplateSizeType
  defaultContentTypes: Record<TemplateContentType, ITemplateLayerItem>
  templateFields: ITemplateFieldGroups | Record<string, never>
}

export const useTemplateStore = defineStore('template', {
  state: (): IState => ({
    templateName: '',
    templateType: TemplateTypeEnum.RECEIPT,
    orientation: OrientationEnum.VERTICAL,
    backgroundType: TemplateBackgroundTypeEnum.COLOR,
    background: '',
    colours: [],

    layers: [
      {
        id: 'receipt___header',
        title: 'layers.receipt_header',
        color: '#74a8f5',
        height: 450,
        items: [],
      },
      {
        id: 'receipt___column_titles',
        title: 'layers.receipt_column_titles',
        color: '#BAEC69',
        height: 230,
        items: [],
      },
      {
        id: 'receipt___body',
        title: 'layers.receipt_body',
        color: '#FFB78F',
        height: 350,
        items: [],
      },
      {
        id: 'receipt__payment_summary',
        title: 'layers.payment_summary',
        color: '#F398DF',
        height: 150,
        items: [],
      },
      {
        id: 'receipt___footer',
        title: 'layers.footer',
        color: '#AB9AEF',
        height: 200,
        items: [],
      },
    ],

    defaultContentTypes: {
      text: {
        type: 'text',
        value: 'Add Text',
        start_position: { x: 0, y: 0 },
        rotate: 0,
        width: 100,
        height: 32,
        font_size: 14,
        font_weight: 500,
        text_align: 'left',
        font_family: 'Poppins',
        colour: '#000000',
        is_show: true,
        is_field: false,
        id: 0,
      },
      image: {
        type: 'image',
        value: '',
        start_position: { x: 0, y: 0 },
        radius: 0,
        width: 70,
        height: 70,
        rotate: 0,
        is_show: true,
        is_field: false,
        id: 0,
      },
      shape: {
        type: 'shape',
        value: 'line',
        variant: 'solid',
        start_position: { x: 0, y: 0 },
        rotate: 0,
        width: 70,
        height: 70,
        colour: '#000000',
        is_show: true,
        is_field: false,
        id: 0,
      },
    },
    templateFields: {},

    uploadedImageIds: [],

    newUploadImages: {},

    activeLayer: { index: -1 },

    activeItem: {},

    templateSize: 'a4',
  }),
  actions: {
    handleSetLayers(layers: ITemplateLayer[]) {
      this.layers = layers
    },
    handleSetUploadedImageIds(imagesId: number[]) {
      this.uploadedImageIds = imagesId
    },

    // this is for left section sidebar
    handleToggleShow(layerIndex: number, itemIndex: number) {
      const item = this.layers[layerIndex].items[itemIndex]
      item.is_show = !item.is_show
      if (!item.is_show && item.id === this.activeItem?.id) {
        this.handleActiveItem({})
      } else if (item.is_show) this.handleActiveItem(item)
    },
    // this is for left section sidebar
    handleRowDragged({ newItems, layerIndex }: { newItems: ITemplateLayerItem[]; layerIndex: number }) {
      this.layers[layerIndex].items = newItems
    },
    handleResizeLayer({ layerIndex, height }: Record<string, number>) {
      this.layers[layerIndex].height = height
    },

    handleAddContent({
      type,
      overrideFields = {},
    }: {
      type: TemplateContentType
      overrideFields: Partial<ITemplateLayerItem>
    }) {
      const { index: activeLayerIndex } = this.activeLayer
      if (activeLayerIndex !== -1) {
        const maxStartPositionX = this.isPrintTemplate ? 200 : 500
        const y =
          this.layers[activeLayerIndex].height - 20 > 0
            ? Math.floor(Math.random() * (this.layers[activeLayerIndex].height - 20))
            : 0
        const x = Math.floor(Math.random() * maxStartPositionX)

        this.layers[activeLayerIndex].items.unshift({
          ...this.defaultContentTypes[type],
          ...overrideFields,
          start_position: { y, x },
          id: type !== 'image' ? Date.now() : this.getLastUploadedImageId,
        })
      }
    },
    handleActiveLayer(payload: IActiveLayer) {
      this.activeLayer = payload
    },
    handleActiveItem(item: ITemplateLayerItem | Record<string, never>) {
      this.activeItem = item
    },
    handleMoveItem({ layerIndex, y, x, itemIndex }: Record<string, number>) {
      const item = this.layers[layerIndex].items[itemIndex]
      item.start_position.x += x
      item.start_position.y += y

      this.handleItemPositionValidating({ layerIndex, itemIndex })
    },

    handleItemPositionValidating({ layerIndex, itemIndex }: Record<string, number>) {
      const layer = this.layers[layerIndex]
      const item = layer.items[itemIndex]

      if (!item) return

      if (item.start_position.x < 0) item.start_position.x = 0
      if (item.start_position.y < 0) item.start_position.y = 0

      if (item.start_position.x + item.width > this.templateWidth)
        item.start_position.x = this.templateWidth - item.width
      if (item.start_position.y + item.height > layer.height) item.start_position.y = layer.height - item.height
    },

    handleUpdateItemSetting(field: keyof ITemplateLayerItem, value: TemplateItemLayerValue) {
      const layer = this.layers[this.activeLayer.index]
      const itemIndex = layer.items.findIndex((layerItem) => layerItem.id === this.activeItem?.id)

      if (itemIndex === -1) return { itemIndex }

      layer.items[itemIndex][field] = value as never
      // this.activeItem[field] = value
      return { itemIndex }
    },
    handleDeleteItem({ layerIndex, itemIndex }: Record<string, number>) {
      const item = this.layers[layerIndex].items[itemIndex]

      if (item.type === 'image') {
        const uploadImagesIndex = this.uploadedImageIds.findIndex((id) => id === item.id)
        if (uploadImagesIndex !== -1) {
          this.uploadedImageIds.splice(uploadImagesIndex, 1)
          delete this.newUploadImages[item.id]
        }
      }

      this.layers[layerIndex].items.splice(itemIndex, 1)
      this.activeItem = {}
    },

    handleSetNewImage({ id, blob, file }: { id: number | string; blob: string; file: File }) {
      this.newUploadImages[id] = { blob, file }
    },

    handleSetBackground(background: Background) {
      this.background = background
    },

    // this action use for clear newUploadImages state after user upload template
    handleResetNewImages() {
      this.newUploadImages = {}
    },
    handleSetTemplateSize(size: TemplateSizeType) {
      this.templateSize = size
    },
    handleSetDigitalMenuKeys(template: ITemplate) {
      this.background = template.background
      this.colours = template.colours
      this.backgroundType = template.background_type
      this.orientation = template.orientation
      this.templateType = template.template_type
    },
    handleSetTemplateFields(fields: ITemplateFieldGroups) {
      this.templateFields = fields
    },
    handleSetTemplateName(name: string) {
      this.templateName = name
    },

    initDigitalMenu() {
      this.templateType = TemplateTypeEnum.DIGITAL_MENU
      this.layers = [
        {
          id: 'receipt___header',
          title: 'layers.receipt_header',
          color: '#74a8f5',
          height: 450,
          items: [],
        },
      ]
    },
  },
  getters: {
    getLastUploadedImageId(): number {
      return this.uploadedImageIds[this.uploadedImageIds.length - 1] || 0
    },
    isPrintTemplate(): boolean {
      return this.templateSize === 'receipt'
    },
    isDigitalMenu(): boolean {
      return this.templateType === TemplateTypeEnum.DIGITAL_MENU
    },

    isVertical(): boolean {
      return this.orientation === OrientationEnum.VERTICAL
    },

    templateWidth(): number {
      return this.isDigitalMenu ? (this.isVertical ? 450 : 800) : this.isPrintTemplate ? 390 : 720
    },
  },
})
